import React, { useState } from 'react';
import { useLeadExecutions } from '../hooks/useLeadExecutions';
import { updateLeadExecutionStage, updateLeadExecutionSiteExc, updateLeadExecutionProjectValue } from '../services/leadExecutionService';
import { getLeadExecutionColumns } from '../components/leadExecutionColumns';
import { getLeadExecutionFinancialColumns } from '../components/lead_execution_column_2';
import { useAuthStore } from '@/store/authStore';
import { useUsers } from '@/hooks/useUsers';
import { LeadExecutionStats } from '../components/LeadExecutionStats';
import { LeadExecutionFilter } from '../components/LeadExecutionFilter';
import { LeadExecutionTableView } from '../components/LeadExecutionTableView';
import { useNavigate } from 'react-router-dom';
import { ViewLead } from '../../verve-leads/leads/components/ViewLead';
import { EditProjectValueModal } from '../components/EditProjectValueModal';
import toast from 'react-hot-toast';

export const LeadExecutionsScreen = () => {
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const leadData = useLeadExecutions(); 
  const { teamMembers: projectManagers } = useUsers({ role: 'designer', limit: 1000 });
  const { teamMembers: designers } = useUsers({ role: 'designer', limit: 1000 });
  const [selectedLeadId, setSelectedLeadId] = useState(null);
  const [editingExecution, setEditingExecution] = useState(null);
  const [isEditingValue, setIsEditingValue] = useState(false);

  const handleStageChange = async (rowId, newStatus) => {
    try {
      await updateLeadExecutionStage(rowId, newStatus);
      leadData.refetch(); 
      toast.success('Stage updated successfully');
    } catch (err) {
      console.error(err);
      toast.error('Failed to update stage');
    }
  };
  const handleSiteExcChange = async (leadId, newSiteExc) => {
    try {
      await updateLeadExecutionSiteExc(leadId, newSiteExc);
      leadData.refetch();
      toast.success('Site Executive assigned successfully');
    } catch (err) {
      console.error(err);
      toast.error('Failed to assign Site Executive');
    }
  };

  const handleEditProjectValue = async (id, value) => {
    try {
      setIsEditingValue(true);
      await updateLeadExecutionProjectValue(id, value);
      await leadData.refetch();
      toast.success('Project Value updated successfully');
      setEditingExecution(null);
    } catch (err) {
      toast.error('Failed to update project value');
    } finally {
      setIsEditingValue(false);
    }
  };
  
  const allColumns1 = getLeadExecutionColumns({ 
    user, 
    projectManagers,
    designers,
    onSiteExcChange: handleSiteExcChange,
    onView: (row) => setSelectedLeadId(row.leadId),
    onEdit: (row) => setEditingExecution(row),
    onAdd: (row, type) => {
      if (type === 'initial_quotation' || type === 'budget') {
        navigate('/budget', { state: { lead: row.leadId } });
      } else if (type === 'order_placing') {
        navigate('/ordering', { state: { lead: row.leadId } });
      }
    }
  });
  const actionsCol = allColumns1.find(c => c.key === 'actions');
  const columns1 = allColumns1.filter(c => c.key !== 'actions');
  const columns2 = getLeadExecutionFinancialColumns({ onStageChange: handleStageChange });
  const columns = [...columns1, ...columns2, actionsCol].filter(Boolean);

  return (
    <div className="flex flex-col gap-4 w-full min-w-0 max-w-full animate-in fade-in duration-700 relative">
      <div className="relative overflow-hidden w-full h-32 bg-white border border-slate-200 rounded-xl flex items-center justify-between shadow-sm">
        <div className="pl-8 z-20">
          <h1 className="font-serif font-medium text-3xl text-slate-900 tracking-wide">
            Lead Executions
          </h1>
          <p className="text-sm font-body text-slate-400 mt-2 font-medium">Manage converted leads and track executions</p>
        </div>
        <div className="w-[50%] h-full relative overflow-hidden flex-shrink-0">
          <div className="absolute inset-0 bg-gradient-to-r from-white via-white/70 to-transparent z-10 w-48" />
          <img
            src="/kitchens/1.jpg"
            alt="Lead Executions"
            className="absolute top-0 right-0 w-full h-full object-cover object-center opacity-90"
          />
        </div>
      </div>

      <LeadExecutionStats stats={leadData.stats} />

      <LeadExecutionFilter {...leadData} />

      <LeadExecutionTableView 
        columns={columns}
        executions={leadData.executions}
        isLoading={leadData.isLoading}
        onRowClick={(row) => navigate(`/lead-executions/${row._id}`)}
        page={leadData.page}
        setPage={leadData.setPage}
        totalPages={leadData.totalPages}
        totalItems={leadData.totalItems}
      />

      {selectedLeadId && (
        <ViewLead 
          isOpen={!!selectedLeadId}
          onClose={() => setSelectedLeadId(null)}
          lead={selectedLeadId}
          user={user}
        />
      )}

      {editingExecution && (
        <EditProjectValueModal
          isOpen={!!editingExecution}
          onClose={() => setEditingExecution(null)}
          execution={editingExecution}
          onSave={handleEditProjectValue}
          isLoading={isEditingValue}
        />
      )}
    </div>
  );
};
